import React, { useContext, useEffect, useMemo, useState } from 'react';
import styles from '@buddieshr/styles/components/addToSlackButton.module.scss';
import { Button } from 'antd';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import SourceContext from '../../sourceContext';
import { getInstallLink } from '@buddieshr/utils/utils';
import { ANALYTICS_EVENTS, LOCAL_STORAGE } from '@buddieshr/utils/constants';
import { gtagEvent } from '@buddieshr/utils/gtag';
import { oaiqCheckoutStarted } from '@buddieshr/utils/oaiq';
import { useRouter } from 'next/router';

const AddToSlackButton = ({
  medium = false,
  small = false,
  text = null,
  style = {},
  app,
  block = false,
  refSuffix = '',
  bhVariant = null,
}) => {
  const { t } = useTranslation();
  const refSource = useContext(SourceContext);
  const [installLink, setInstallLink] = useState(null);
  const router = useRouter();
  const appSlug = app?.slug || '';
  const [hasCompletedGetStartedFlow, setHasCompletedGetStartedFlow] = useState(false);

  useEffect(() => {
    setInstallLink(getInstallLink(refSource, app.installSlackLink));
  }, [app, refSource]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const detectCompletion = () => {
      try {
        const storedFlag =
          window.localStorage.getItem(LOCAL_STORAGE.GET_STARTED_COMPLETED) || '';
        const sanitizedFlag = storedFlag.trim().toLowerCase();
        const hasFlag =
          sanitizedFlag === 'true' ||
          sanitizedFlag === '1' ||
          sanitizedFlag === 'yes';
        const storedEmail =
          window.localStorage.getItem('get_started_business_email') || '';
        setHasCompletedGetStartedFlow(hasFlag || storedEmail.length > 0);
      } catch (error) {
        setHasCompletedGetStartedFlow(false);
      }
    };

    detectCompletion();
    const onStorage = (event) => {
      if (!event || typeof event.key !== 'string') {
        return;
      }

      if (
        event.key === LOCAL_STORAGE.GET_STARTED_COMPLETED ||
        event.key === 'get_started_business_email'
      ) {
        detectCompletion();
      }
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const finalInstallLink = useMemo(() => {
    if (!app?.installSlackLink) {
      return null;
    }

    let baseTrackedLink = installLink;

    if (!baseTrackedLink) {
      if (
        typeof refSource === 'string' &&
        refSource.length > 0 &&
        refSource !== 'unset'
      ) {
        baseTrackedLink = getInstallLink(refSource, app.installSlackLink);
      } else {
        const separator = app.installSlackLink.includes('?') ? '&' : '/?';
        baseTrackedLink = `${app.installSlackLink}${separator}ref=`;
      }
    }

    return `${baseTrackedLink}${refSuffix}`;
  }, [app?.installSlackLink, installLink, refSource, refSuffix]);

  const shouldGateInstall = useMemo(() => {
    if (hasCompletedGetStartedFlow) {
      return false;
    }
    if (typeof refSource !== 'string') {
      return false;
    }
    const ref = refSource.toLowerCase();
    return ref.includes('gads') || ref.includes('fbads');
  }, [hasCompletedGetStartedFlow, refSource]);

  const gatedDestination = useMemo(() => {
    if (!shouldGateInstall) {
      return null;
    }
    const basePath = '/get-started-form';
    if (!finalInstallLink) {
      return basePath;
    }
    const query = new URLSearchParams({
      redirect: finalInstallLink,
      app: appSlug,
    });
    return `${basePath}?${query.toString()}`;
  }, [appSlug, finalInstallLink, shouldGateInstall]);

  const handleClick = (event) => {
    if (!app.installSlackLink) {
      return;
    }

    gtagEvent({
      // action: ANALYTICS_EVENTS.ADD_TO_SLACK,
      action: `${ANALYTICS_EVENTS.ADD_TO_SLACK}_${appSlug.toUpperCase()}`,
      category: 'register',
      label: 'AddToSlackButton',
    });
    gtagEvent({
      action: ANALYTICS_EVENTS.ADD_TO_SLACK,
      category: 'register',
      label: 'AddToSlackButton',
    });
    oaiqCheckoutStarted({ id: appSlug, name: app.name });

    if (shouldGateInstall) {
      event.preventDefault();
      if (gatedDestination) {
        router.push(gatedDestination);
      }
    }
  };

  return (
    <>
      <a
        href={
          app.installSlackLink
            ? shouldGateInstall
              ? gatedDestination
              : finalInstallLink
            : null
        }
        disabled={!app.installSlackLink}
        target={shouldGateInstall ? '_self' : '_blank'}
        rel="noopener noreferrer"
        className={
          bhVariant
            ? `bh-btn ${bhVariant}`
            : `${styles.mainLink} ${small || medium ? styles.small : ''}`
        }
        style={bhVariant ? style : undefined}
        onClick={handleClick}
      >
        {bhVariant ? (
          <>
            {text || t('addToSlack')}
            <Image
              className={styles.slackIconInButton}
              src="/img/slack_icon.png"
              width={20}
              height={20}
              alt="connect employees on slack"
            />
          </>
        ) : (
          <Button
            type="primary"
            size={small ? 'small' : medium ? '' : 'large'}
            style={style}
            className={styles.mainCTA}
            {...(block ? { block: true } : {})}
            disabled={!app.installSlackLink}
          >
            <div className={styles.inner}>
              {text || t('addToSlack')}
              &nbsp;
              <Image
                className={styles.slackIconInButton}
                src="/img/slack_icon.png"
                width={small ? 16 : 24}
                height={small ? 16 : 24}
                alt="connect employees on slack"
              />
            </div>
          </Button>
        )}
      </a>
      {!app.installSlackLink && (
        <div
          style={{
            color: 'grey',
            fontSize: 12,
            margin: 4,
          }}
        >
          Coming soon
        </div>
      )}
    </>
  );
};

export default AddToSlackButton;
